/**
 * State Types for UI-Model-1 (API-Direct Approach)
 *
 * This module defines the state shape that uses API types DIRECTLY.
 * No normalization layer - API format flows through the entire system.
 *
 * Architecture:
 * - Fetch → Store API response as-is in state
 * - Render → Components read from API format state
 * - Edit → Commands modify API format state directly
 * - Save → Send state sections to API (already in correct format)
 */

// Import from local authoritative types (NOT legacy UI-Model)
import type {
  UIModelAPI,
  ContainerAPI,
  FieldAPI,
  WidgetAPI,
  CategoryAPI,
  ConnectorAPI,
  GroupAPI,
  PageAPI,
  CanvasAPI,
  CanvasMapAPI,
  HeaderAPI,
  FooterAPI,
  NavigationAPI,
  NavigationConfigurationAPI,
  ContainerConfigurationAPI,
  ContainerContentAPI,
  SharedWidgetsAPI,
  ItemAPI,
  ItemType,
  FieldType,
  WidgetType,
  SubmodelAPI,
  SubmodelFullAPI,
  SubmodelRefAPI,
  ContainerMetadataAPI,
  UsedComponentAPI,
} from './api.types';

// Import type guards and factory functions for local use AND re-export
import {
  isFieldAPI as _isFieldAPI,
  isWidgetAPI as _isWidgetAPI,
  isCategoryAPI as _isCategoryAPI,
  isConnectorAPI as _isConnectorAPI,
  isItemAPI as _isItemAPI,
  isSubmodelFull as _isSubmodelFull,
  isSubmodelRef as _isSubmodelRef,
  createContainerAPI,
  createGroupAPI,
  createPageAPI,
  createCanvasAPI,
  createFieldAPI as _createFieldAPI,
  createWidgetAPI as _createWidgetAPI,
  createCategoryAPI as _createCategoryAPI,
  createConnectorAPI as _createConnectorAPI,
  createSubmodelFullAPI as _createSubmodelFullAPI,
  createSubmodelRefAPI as _createSubmodelRefAPI,
  DEFAULT_CONTAINER_CONFIGURATION as API_CONTAINER_CONFIG,
  DEFAULT_HEADER_CONFIGURATION as API_HEADER_CONFIG,
  DEFAULT_FOOTER_CONFIGURATION as API_FOOTER_CONFIG,
  DEFAULT_NAVIGATION_CONFIGURATION as API_NAV_CONFIG,
} from './api.types';

// Re-export API types for convenience
export type {
  UIModelAPI,
  ContainerAPI,
  FieldAPI,
  WidgetAPI,
  CategoryAPI,
  ConnectorAPI,
  GroupAPI,
  PageAPI,
  CanvasAPI,
  CanvasMapAPI,
  HeaderAPI,
  FooterAPI,
  NavigationAPI,
  NavigationConfigurationAPI,
  ContainerConfigurationAPI,
  ContainerContentAPI,
  SharedWidgetsAPI,
  ItemAPI,
  ItemType,
  FieldType,
  WidgetType,
  SubmodelAPI,
  SubmodelFullAPI,
  SubmodelRefAPI,
  ContainerMetadataAPI,
  UsedComponentAPI,
};

// Re-export type guards and factory functions
export {
  _isFieldAPI as isFieldAPI,
  _isWidgetAPI as isWidgetAPI,
  _isCategoryAPI as isCategoryAPI,
  _isConnectorAPI as isConnectorAPI,
  _isItemAPI as isItemAPI,
  _isSubmodelFull as isSubmodelFull,
  _isSubmodelRef as isSubmodelRef,
  createContainerAPI,
  createGroupAPI,
  createPageAPI,
  createCanvasAPI,
  _createFieldAPI as createFieldAPI,
  _createWidgetAPI as createWidgetAPI,
  _createCategoryAPI as createCategoryAPI,
  _createConnectorAPI as createConnectorAPI,
  _createSubmodelFullAPI as createSubmodelFullAPI,
  _createSubmodelRefAPI as createSubmodelRefAPI,
};

// Legacy type aliases for backwards compatibility with existing code
export type HeaderConfigAPI = HeaderAPI;
export type FooterConfigAPI = FooterAPI;
export type NavigationConfigAPI = NavigationConfigurationAPI;

// ============================================================================
// Used Field Tracking (For Global Field Uniqueness)
// ============================================================================

/**
 * Information about a field's current usage in the model.
 * Used for O(1) lookup to prevent duplicate field additions.
 */
export interface UsedFieldInfo {
  /** The container ID where the field is placed */
  containerId: string;
  /** The container name for display in tooltips */
  containerName: string;
  /** The instantiated item ID in the container */
  itemId: string;
  /** The parent submodel ID if the field comes from a submodel */
  parentId?: string;
  /** The page name where the container exists (for enhanced tooltip display) */
  pageName?: string;
}

/**
 * Information about a submodel's placement in the model.
 * Used for page-locking (Rule 2) and container assignment (Rule 1).
 */
export interface SubmodelPlacementInfo {
  submodelId: string;
  containerId: string;
  containerName: string;
  canvasId: string;
  pageName?: string;
}

// ============================================================================
// Editor State (Not saved to API)
// ============================================================================

export interface EditorState {
  activeGroupId: string | null;
  activePageId: string | null;
  selectedContainerId: string | null;
  selectedItemId: string | null;
  selectedItemType: ItemType | 'submodel' | null;
  selectedSubmodelId: string | null;
  selectedSection: 'header' | 'footer' | 'navigation' | 'header-your-build' | null;
  selectionPath: string[];
  // Navigation element selection (group/page config panels)
  selectedGroupId: string | null;
  selectedPageId: string | null;
  selectedPageGroupId: string | null; // Parent group of selected page
}

// ============================================================================
// History State (For Undo/Redo - Snapshot Based)
// ============================================================================

export interface HistoryEntry {
  timestamp: number;
  description: string;
  snapshot: UIModelAPI;  // Full model snapshot for undo/redo
}

export interface HistoryState {
  past: HistoryEntry[];
  future: HistoryEntry[];
  maxSize: number;
}

// ============================================================================
// Dirty Tracking (For Save Queue)
// ============================================================================

export type DirtySection =
  | 'header'
  | 'footer'
  | 'navigation'
  | 'canvas'
  | 'theme'
  | 'customCss'
  | 'sharedWidgets';

export interface DirtyTrackerState {
  sections: Set<DirtySection>;
  canvasPages: Set<string>;  // canvasIds that need saving
  lastSaveTimestamp: Record<DirtySection, number>;
  pendingSaveCount: number;
}

// ============================================================================
// Main State Shape
// ============================================================================

/**
 * UIModelState - The main state shape for UI-Model-1
 *
 * Key principle: model uses API format directly, no transformation needed
 */
export interface UIModelState {
  // === DIRECTLY FROM API (UIModelAPI structure) ===
  model: UIModelAPI | null;

  // === EDITOR-ONLY STATE (not saved to API) ===
  editorState: EditorState;

  // History for undo/redo (editor-only, snapshot-based)
  history: HistoryState;

  // Dirty tracking for save queue (editor-only)
  dirtyTracker: DirtyTrackerState;

  // Used fields tracking for global uniqueness (editor-only)
  // Key: originalFieldId (parentId-fieldName for submodel fields, fieldId for root fields)
  // Provides O(1) lookup for checking if a field is already used
  usedFieldsMap: Map<string, UsedFieldInfo>;

  // Used categories tracking for global uniqueness (editor-only)
  // Key: parentId-categoryId for submodel categories, categoryId for root categories
  // Provides O(1) lookup for checking if a category is already used
  usedCategoriesMap: Map<string, UsedFieldInfo>;

  // Used connectors tracking for global uniqueness (editor-only)
  // Key: parentId-connectorName for submodel connectors, connectorName for root connectors
  // Provides O(1) lookup for checking if a connector is already used
  usedConnectorsMap: Map<string, UsedFieldInfo>;

  // Submodel placement tracking (for page-locking and container assignment)
  // Key: submodelId → placement info (which container/page the submodel is placed on)
  submodelPlacementMap: Map<string, SubmodelPlacementInfo>;
  // Key: containerId → submodelId (which submodel is assigned to a container)
  containerToSubmodelMap: Map<string, string>;

  // Loading/error state
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  lastSaveError: string | null;
}

// ============================================================================
// Save Operation Types
// ============================================================================

export interface SaveOperation {
  id: string;
  type: DirtySection;
  canvasId?: string;  // For canvas saves
  data: unknown;
  priority: number;
  retryCount: number;
  maxRetries: number;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  createdAt: number;
  updatedAt: number;
  error?: string;
}

export interface SaveQueueState {
  operations: SaveOperation[];
  isProcessing: boolean;
  lastProcessedAt: number | null;
}

// ============================================================================
// Default Values
// ============================================================================

export const DEFAULT_EDITOR_STATE: EditorState = {
  activeGroupId: null,
  activePageId: null,
  selectedContainerId: null,
  selectedItemId: null,
  selectedItemType: null,
  selectedSubmodelId: null,
  selectedSection: null,
  selectionPath: [],
  selectedGroupId: null,
  selectedPageId: null,
  selectedPageGroupId: null,
};

export const DEFAULT_HISTORY_STATE: HistoryState = {
  past: [],
  future: [],
  maxSize: 50,
};

export const createDefaultDirtyTracker = (): DirtyTrackerState => ({
  sections: new Set(),
  canvasPages: new Set(),
  lastSaveTimestamp: {
    header: 0,
    footer: 0,
    navigation: 0,
    canvas: 0,
    theme: 0,
    customCss: 0,
    sharedWidgets: 0,
  },
  pendingSaveCount: 0,
});

export const createInitialState = (): UIModelState => ({
  model: null,
  editorState: DEFAULT_EDITOR_STATE,
  history: DEFAULT_HISTORY_STATE,
  dirtyTracker: createDefaultDirtyTracker(),
  usedFieldsMap: new Map(),
  usedCategoriesMap: new Map(),
  usedConnectorsMap: new Map(),
  submodelPlacementMap: new Map(),
  containerToSubmodelMap: new Map(),
  isLoading: false,
  isSaving: false,
  error: null,
  lastSaveError: null,
});

// ============================================================================
// Default Configurations (for creating new elements)
// Re-export defaults from api.types, with local overrides for backwards compatibility
// ============================================================================

// Local defaults (backwards compatible with existing code)
export const DEFAULT_CONTAINER_CONFIGURATION: ContainerConfigurationAPI = {
  ...API_CONTAINER_CONFIG,
};

export const DEFAULT_HEADER_CONFIGURATION: HeaderConfigAPI = {
  configuration: {
    ...API_HEADER_CONFIG,
  },
};

export const DEFAULT_FOOTER_CONFIGURATION: FooterConfigAPI = {
  configuration: {
    ...API_FOOTER_CONFIG,
  },
};

export const DEFAULT_NAVIGATION_CONFIGURATION: NavigationConfigAPI = {
  ...API_NAV_CONFIG,
};

export const DEFAULT_THEME_CONFIGURATION = {
  solidButtonBackground: '#0747A6',
  solidButtonLabel: '#ffffff',
  borderButtonBorder: '#0747A6',
  borderButtonLabel: '#0747A6',
  textButtonLabel: '#0747A6',
  titleColor: '#0747A6',
  subtitleColor: '#172B4D',
  bodyTextColor: '#6D788C',
  mainBackgroundColor: '#ffffff',
  headerBackgroundColor: '#ffffff',
  navigationBackgroundColor: '#ffffff',
};

export const DEFAULT_CUSTOM_CSS = {
  customStyles: {},
  globalStyles: '',
  css: '',
  enabled: true,
};

// ============================================================================
// Factory Functions for Creating New Elements
// Note: These wrap the api.types factory functions for backwards compatibility
// ============================================================================

export const createContainer = (
  id: string,
  name: string,
  order: number,
  overrides?: Partial<ContainerAPI>
): ContainerAPI => createContainerAPI(id, name, order, 0, overrides);

export const createGroup = (
  id: string,
  name: string,
  order: number,
  overrides?: Partial<GroupAPI>
): GroupAPI => createGroupAPI(id, name, order, overrides);

export const createPage = (
  id: string,
  name: string,
  canvasId: string,
  order: number,
  overrides?: Partial<PageAPI>
): PageAPI => createPageAPI(id, name, canvasId, order, overrides);

export const createCanvas = (
  pageId: string,
  groupId: string
): CanvasAPI => createCanvasAPI(pageId, groupId);

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Actions that can be undone/redone
 */
export type UndoableAction =
  | 'ADD_CONTAINER'
  | 'REMOVE_CONTAINER'
  | 'UPDATE_CONTAINER'
  | 'MOVE_CONTAINER'
  | 'ADD_ITEM'
  | 'REMOVE_ITEM'
  | 'UPDATE_ITEM'
  | 'MOVE_ITEM'
  | 'ADD_GROUP'
  | 'REMOVE_GROUP'
  | 'UPDATE_GROUP'
  | 'ADD_PAGE'
  | 'REMOVE_PAGE'
  | 'UPDATE_PAGE'
  | 'BATCH';

/**
 * Path to an element in the nested API structure
 */
export interface ElementPath {
  canvasId: string;
  containerPath: string[];  // Array of container IDs from root to element
  itemId?: string;
}

/**
 * Result of a container/item lookup
 */
export interface LookupResult<T> {
  element: T | undefined;
  path: ElementPath | null;
  parent: ContainerAPI | null;
}
